import type { Step } from 'react-joyride';

// Catalog of guided tours. Each new feature wave adds an entry here. The
// version field bumps monotonically per id whenever the tour content
// materially changes — users who completed an older version of the same id
// will be re-offered the tour.
//
// Per the feature-launch UX rule: every new OneDesk feature ships with a
// tour entry. Don't merge a feature without one.
export type Tour = {
  id: string;
  version: string;
  /** Shown in the "What's new" banner + the help-menu replay list. */
  title: string;
  /** One-line teaser for the banner before the user starts the tour. */
  blurb: string;
  steps: Step[];
};

export const TOURS: Tour[] = [
  {
    id: 'wave-1-widgets',
    version: '1',
    title: 'At-a-glance widgets',
    blurb: 'Your day, your inbox, your SLA — all on the Home page.',
    steps: [
      {
        target: '[data-tour="widgets-row"]',
        title: 'New: at-a-glance row',
        content:
          'These four cards summarise the things you ask about every day. They are marked Soon for now — real data lands once the integration is wired.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="widget-today"]',
        title: 'Today',
        content:
          'Your next three meetings, with one-click join for Teams. Sourced from your Microsoft calendar.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="widget-inbox"]',
        title: 'Inbox',
        content:
          'Unread mail count and your top senders today, so you know who is waiting before you open Outlook.',
        placement: 'bottom',
      },
      {
        target: '[data-tour="widget-sla"]',
        title: 'My SLA today',
        content:
          'Pending, at-risk and breached emails for you personally — pulled from the SLA Email Tracker.',
        placement: 'bottom',
      },
    ],
  },
];

// Returns the first tour the user hasn't completed (or seen the latest
// version of). null when they're caught up.
export const pendingTour = (toursSeen: Record<string, string> | undefined): Tour | null => {
  const seen = toursSeen ?? {};
  for (const t of TOURS) {
    if (seen[t.id] !== t.version) return t;
  }
  return null;
};

export const tourById = (id: string): Tour | undefined => TOURS.find((t) => t.id === id);
